import {loadResource} from "@lib/utils/api";
const calendarRes = loadResource("/conf/calc/calendar.json");

const MILLISECONDS_TO_DAYS_CONSTANT = 24 * 60 * 60 * 1000;

class DatePoint {
    /**
     * @param {any} stringOrDate string, Date, DatePoint or non-string falsy value for new Date() 
     * @param {boolean} silent - disable printing errors in the console
     * string can be in straight (dd.mm.yyyy) or inverse (yyyy.mm.dd) format
     */
    constructor(stringOrDate, silent) {
        silent = silent || false;
        if (typeof (stringOrDate) === "string") {
            stringOrDate = stringOrDate.replace(/[^\d.]/g, "");
            const straight = /^(\d\d)[.](\d\d)[.](\d{4})$/.exec(stringOrDate);
            if (straight) {
                this.date = new Date(parseInt(straight[3], 10), parseInt(straight[2], 10) - 1, parseInt(straight[1]));
            } else {
                const inverse = /^(\d{4})[.](\d\d)[.](\d\d)$/.exec(stringOrDate);
                if (inverse !== null) {
                    this.date = new Date(parseInt(inverse[1], 10), parseInt(inverse[2], 10) - 1, parseInt(inverse[3]));
                } else {
                    if (!silent)
                        throw new Error(`Wrong format of date in string: ${stringOrDate}`);

                }
            }
        }
        else if (stringOrDate instanceof Date) {
            this.date = new Date(stringOrDate);
        }
        else if (stringOrDate instanceof DatePoint) {
            this.date = new Date(stringOrDate.date);
        }
        else if (!stringOrDate)
            this.date = new Date();
        else {
            if (!silent)
                throw new Error("Wrong argument type in DatePoint contructor.");
        }

        this.date?.setHours(12,0,0,0);
    }

    get valid() {
        return !!(this.date !== undefined && this.date instanceof Date && this.date.getDate());
    }

    toInt() {
        return 384 * this.date.getFullYear() + 32 * this.date.getMonth() + this.date.getDate();
    }

    static fromInt(v) {
        const year = parseInt(v / 384);
        const month = parseInt((v % 384) / 32);
        const day = v % 32;
        return new DatePoint(`${day}.${month + 1}.${year}`);
    }

    valueOf() {
        return this.toInt();
    }

    toString() {
        return this.toLocaleFormat();
    }

    copy() {
        return new DatePoint(this.date);
    }

    isEqualTo(other) {
        if (!(other instanceof DatePoint)) {
            other = new DatePoint(other);
        }
        return (this.valid && other.valid && this.toInt() == other.toInt());
    }

    fixDayMonthYear(day, month, year) {
        const result = this.copy();
        if (year != null) {
            result.date.setFullYear(year);
        }
        if (month != null) {
            result.date.setMonth(month - 1);
        }
        if (day != null) {
            result.date.setDate(day);
        }
        return result;
    }

    getSpan(otherPoint) {
        var dateFrom = this.date <= otherPoint.date ? this : otherPoint;
        var dateTo = this.date <= otherPoint.date ? otherPoint : this;
        return new DateSpan(dateFrom, dateTo);
    }

    addSpan(span) {
        var result = this.copy();
        if (span.getDayCount() != undefined) {
            result.inc(span.getDayCount());
        } else {
            var count = span.getWorkingDayCount();
            var inc = (count > 0 ? 1 : -1);
            count *= inc;
            for (let i = 0; i < count; ++i) {
                result.inc(inc);
                while (result.isDayOff())
                    result.inc(inc);
            }
        }
        return result;
    }

    goesBefore(other) {
        if (!(other instanceof DatePoint)) {
            other = new DatePoint(other);
        }
        return this.date < other.date;
    }

    distanceTo(other, includeEnd = true) {
        const result = Math.floor((other.date - this.date) / MILLISECONDS_TO_DAYS_CONSTANT + 0.5);
        return includeEnd ? result + 1 : result;
    }

    getDayNumberOfYear() {
        return Math.floor((this.date - getTheFirstOfJanuary(this.date)) / MILLISECONDS_TO_DAYS_CONSTANT);
    }

    getWeekNumberOfYear() {
        const firstJan = getTheFirstOfJanuary(this.date);
        return Math.ceil((((this.date - firstJan) / MILLISECONDS_TO_DAYS_CONSTANT) + firstJan.getDay()) / 7);
    }

    addDays(amount) {
        var result = this.copy();
        result.date.setDate(this.date.getDate() + amount);
        return result;
    }

    inc(amount) {
        this.date.setDate(this.date.getDate() + (amount ?? 1));
        return this;
    }

    dec() { return this.inc(-1); }

    toLocaleFormat() {
        return this.date.toLocaleDateString(tools.appLocale); //dd.mm.yyyy
    }

    getDayName() {
        var days = ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"];
        return days[this.date.getDay()];
    }

    toVerboseString(params) {
        params = params || {};
        params.addWeekDay = (params.addWeekDay === undefined ? true : params.addWeekDay);
        params.addYear = (params.addYear === undefined ? true : params.addYear);
        var monthNames = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
        var result = "" + this.date.getDate() + " " + monthNames[this.date.getMonth()];
        if (params.addYear) {
            result += " " + this.date.getFullYear() + " года";
        }
        if (params.addWeekDay) {
            result += ", " + this.getDayName(this.date);
        }
        return result; //E.g. 11 сентября 2019 года, воскресенье
    }

    getDaysInMonth() {
        if (this.date.getMonth() === 11) {
            return new Date(this.date.getFullYear() + 1, 0, 0).getDate();
        } else {
            return new Date(this.date.getFullYear(), this.date.getMonth() + 1, 0).getDate();
        }
    }

    /*
    *  Sets date not changing month and year.
    *  It is useful in case when you want to set date 31 for feb month:
    *      1 march will be chosen unlike in original method (2 or 3 march).
    */
    setDateGently(date, monthIsolation) {
        monthIsolation = monthIsolation || false;
        var maxDate = this.getDaysInMonth();
        if (date <= maxDate) {
            this.date.setDate(date);
        } else {
            this.date.setDate(maxDate + (monthIsolation ? 0 : 1));
        }
    }

    /*
    *  Adds a given number of months to the initial date. 
    *  If there is a gap between the initial date and a calculated one, it sets the last day of the new month.
    */
    addMonths(months) {
        if (months) {
            const monthSum = this.date.getMonth() + months;
            const addYears = Math.floor(monthSum / 12);
            const newMonth = monthSum - addYears * 12;
            const currentDay = this.date.getDate();
            if (addYears !== 0) {
                this.date.setYear(this.date.getFullYear() + addYears);
            }
            this.date.setMonth(newMonth);
            if (this.date.getMonth() !== newMonth) {
                this.date.setMonth(newMonth);
            }
            this.setDateGently(currentDay, true);
        }
    }

    predictIsDayOff() {
        const regularHolidays = [
            "|1|2|3|4|5|6|7|8|",
            "|23|",
            "|8|",
            "||",
            "|1|9|",
            "|12|",
            "||",
            "||",
            "||",
            "||",
            "|4|",
            "||"
        ];

        if ([0, 6].indexOf(this.date.getDay()) >= 0)
            return true;
        if (regularHolidays[this.date.getMonth()].indexOf("|" + this.date.getDate() + "|") >= 0)
            return true;
        return false;
    }

    isDayOff() {
        if (calendarRes.loading) {
            return this.predictIsDayOff();
        }
        const calendar = calendarRes.content;
        for (var i = calendar.length - 1; i >= 0; i--) {
            if (this.date.getFullYear() === calendar[i].year) {
                return (calendar[i].daysOff[this.date.getMonth()].indexOf("|" + this.date.getDate() + "|") >= 0);
            }
        }
        if (this.date.getFullYear() > calendar[calendar.length - 1].year)
            return this.predictIsDayOff();
        return false;
    }

    static currentYear() {
        return (new Date()).getFullYear();
    }
}

class DateSpan {
    constructor(dateFrom, dateTo) {
        if (dateFrom && dateTo) {
            this._dateFrom = new DatePoint(dateFrom);
            this._dateTo = new DatePoint(dateTo);
        }
    }

    toString() {
        return this.toLocaleFormat();
    }

    isEqualTo(other) {
        if (!(other instanceof DateSpan)) {
            throw new Error("Can't compare DateSpan with " + typeof (other) + ". Try to cast to DateSpan firstly.");
        }

        if (this._boundsSet()) {
            return other._boundsSet() && this.dateFrom.isEqualTo(other.dateFrom) && this.dateTo.isEqualTo(other.dateTo);
        }

        if (other._boundsSet()) {
            return false;
        }

        return this._dayCount === other._dayCount && this._workingDayCount === other._workingDayCount;
    }

    get dateFrom() {
        return this._dateFrom;
    }

    // AG TODO: It's not ideal solution. Come up with a new one.
    // Transparent getters allow to change using only getters
    // Setters must call 'boundUpdated()' or not update anything at all (maybe remove setters???)
    set dateFrom(dateFrom) {
        if (dateFrom.valid) {
            this._workingDayCount = undefined;
            this._dateFrom = dateFrom;
        }
    }

    get dateTo() {
        return this._dateTo;
    }

    set dateTo(dateTo) {
        if (dateTo.valid) {
            this._workingDayCount = undefined;
            this._dateTo = dateTo;
        }
    }

    static fromDays(days) {
        var span = new DateSpan();
        span._dayCount = days;
        return span;
    }

    static fromWorkingDays(daysWorking) {
        var span = new DateSpan();
        span._workingDayCount = daysWorking;
        return span;
    }

    getWorkingDayCount() {
        if (!this._workingDayCount && this._boundsSet()) {
            let count = 0;
            this.iterDays((day) => count += !day.isDayOff());
            this._workingDayCount = count;
        }
        return this._workingDayCount;
    }

    _boundsSet() {
        return this.dateFrom !== undefined && this.dateTo !== undefined;
    }

    copy() {
        if (this._boundsSet())
            return new DateSpan(this.dateFrom.copy(), this.dateTo.copy());

        if (this.getDayCount() !== undefined)
            return DateSpan.fromDays(this.getDayCount());

        if (this.getWorkingDayCount() !== undefined)
            return DateSpan.fromWorkingDays(this.getWorkingDayCount());
    }

    getDayCount() {
        if (this._boundsSet())
            return this.dateFrom.distanceTo(this.dateTo);
        return this._dayCount;
    }

    toLocaleFormat() {
        return this.dateFrom.toLocaleFormat() + " – " + this.dateTo.toLocaleFormat();
    }

    //return string with day count
    getVerboseDayCount() {
        return tools.verboseCountingValue(this.getDayCount(), [["день", "дня", "дней"]]);
    }

    /*
    *  Returns object {valid: true, monthes: [monthes], days: [days]}
    *              or {valid: false, reason: [reason message string]}
    *
    *  Calculates difference between two dates in monthes and residual days.
    *  Argument optional (used to subtract specified count of days from period)
    *  All calculations correct by laws.
    */
    getPeriodDuration(subtractDayCount) {
        subtractDayCount = subtractDayCount || 0;

        //subtracting
        var fixedDayTo = this.dateTo.copy();
        fixedDayTo = fixedDayTo.addDays(-subtractDayCount);

        if (fixedDayTo.goesBefore(this.dateFrom)) {
            if (fixedDayTo.distanceTo(this.dateFrom) == 2) {
                return {valid: true, monthes: 0, days: 0, residPoint: fixedDayTo, inDays: 0};
            }
            return {valid: false, reason: "dateFrom must be less than dayTo - subtractDayCount"};
        }

        // calculating duration
        var months = (fixedDayTo.date.getFullYear() * 12 + fixedDayTo.date.getMonth()) - (this.dateFrom.date.getFullYear() * 12 + this.dateFrom.date.getMonth());
        var residPoint = fixedDayTo.copy();

        residPoint.setDateGently(this.dateFrom.date.getDate());
        if (residPoint.distanceTo(fixedDayTo) < 0) {
            months--;
            residPoint = fixedDayTo.copy();
            residPoint.date.setDate(1);
            residPoint.date.setMonth(residPoint.date.getMonth() - 1);
            residPoint.setDateGently(this.dateFrom.date.getDate());
        } else if (residPoint.date.getDate() == 1 && fixedDayTo.date.getDate() == residPoint.getDaysInMonth()) {
            residPoint.date.setDate(fixedDayTo.date.getDate() + 1);
            months++;
        }

        return {
            valid: true,
            monthes: months,
            days: residPoint.distanceTo(fixedDayTo),
            residPoint: residPoint,
            inDays: this.dateFrom.distanceTo(fixedDayTo)
        };
    }

    //argument of callback should be DatePoint type
    iterDays(callback) {
        if (!this._boundsSet())
            throw new Error("DateSpan bounds are undefined");
        var start = this.dateFrom.copy();
        var index = 0;
        while (this.getDayCount() > index) {
            callback(start, index);
            start.inc();
            index++;
        }
    }

    /*
    *      Returns list of objects with next attributes:
    *          * dateSpan
    *          * type: [add, sub] (see additional `subtract` parameter)
    *          * leftKey (for add only, null for first if first key goes after <from>)
    *          * rightKey (for add only, null for last if last key foes before <to>)
    *          * instance (for sub only)
    *      Parameters:
    *          required parameters:              
    *              * keypoints (list of objects {date: [Date], ...})
    *          additional parameters:
    *              * subtract (list of sorted objects {dateFrom: [Date], dateTo: [Date], ...})
    *      
    */
    getDateSegments(params) {
        if (!tools.checkParams(params, ["keypoints"])) {
            return [];
        }
        var keypoints = params.keypoints.sort((a, b) => ((new DatePoint(a.date)).goesBefore(b.date) ? -1 : 1));

        var point = this.dateFrom;
        var endPoint = this.dateTo;
        var result = [];

        var key = null;
        var subtractionUsed = {};

        function add(from, to, leftKey, rightKey) {
            result.push({span: new DateSpan(from, to), leftKey: leftKey, rightKey: rightKey, type: "add"});
        }

        function subtract(instanceIndex) {
            var instance = params.subtract[instanceIndex];
            result.push({span: new DateSpan(instance.dateFrom, instance.dateTo), instance: instance, type: "sub"});
            subtractionUsed[instanceIndex] = true;
        }

        function addWithSubtractions(start, end, leftKey, rightKey) {
            var point = start;
            params.subtract.map((s) => new DateSpan(s.dateFrom, s.dateTo)).some(function (s, index) {
                if (subtractionUsed[index] !== true) {
                    if (!point.goesBefore(s.dateFrom)) {
                        subtract(index);
                        if (!s.dateTo.goesBefore(point))
                            point = s.dateTo.addDays(1);
                        return (!s.dateTo.goesBefore(end));

                    } else if (!end.goesBefore(s.dateFrom)) {
                        add(point, s.dateFrom.addDays(-1), leftKey, rightKey);
                        subtract(index);
                        point = s.dateTo.addDays(1);
                    } else {
                        return true;
                    }
                } else {
                    if (!s.dateTo.goesBefore(point)) {
                        point = s.dateTo.addDays(1);
                    }
                }
                return (end.goesBefore(point));
            });

            if (!end.goesBefore(point)) {
                add(point, end, leftKey, rightKey);
            }
        }

        function addPeriod(from, to, rightKey) {
            if (params.subtract === undefined)
                add(from, to, key, rightKey || null);
            else
                addWithSubtractions(from, to, key, rightKey || null);
        }

        if (keypoints.length === 0) {
            add(point, endPoint);
        }

        // cases:
        // 1. kkk[]
        // 2. []kkk
        // 3. [kkk]
        // 4. k[k]k
        // 5. k[kk]
        // 6. [kk]k
        // 7. kk[]k
        for (var i = 0; i < keypoints.length; ++i) {
            var keypoint = keypoints[i];
            var date = new DatePoint(keypoint.date);
            if (point.goesBefore(date)) {
                if (endPoint.goesBefore(date)) {  // 2, 4, 6, 7
                    addPeriod(point, endPoint, keypoint);
                    break;
                }
                else {  // 3, 4, 5, 6
                    addPeriod(point, date.addDays(-1), keypoint);
                    point = date;
                    if (i === keypoints.length - 1) {  // 3, 5
                        key = keypoint;
                        addPeriod(point, endPoint);
                    }
                }
            }
            else if (i === keypoints.length - 1) {  // only 1
                key = keypoint;
                addPeriod(point, endPoint);
            }

            key = keypoint;
        }
        return result;
    }

    contains(datePoint) {
        if (!this._boundsSet()) return undefined;
        return !datePoint.goesBefore(this.dateFrom) && !this.dateTo.goesBefore(datePoint);
    }

    intersects(other) {
        if (!this._boundsSet() || !other._boundsSet()) return undefined;
        return !this.dateTo.goesBefore(other.dateFrom) && !other.dateTo.goesBefore(this.dateFrom);
    }

    // check that this fully inside other
    inside(other) {
        if (!this._boundsSet() || !other._boundsSet()) return undefined;
        return !this.dateFrom.goesBefore(other.dateFrom) && !other.dateTo.goesBefore(this.dateTo);
    }
}

/*Parameters:
    * required parameters:              
    *       * from
    *       * to
*/
function makeDateSegmentWorker(segments) {
    var _segments = segments
        .map((a) => new DateSpan(a.from, a.to))
        .sort((a, b) => (a.dateFrom.goesBefore(b.dateFrom) ? -1 : 1));

    return {
        /// exclude off days by default
        checkDay: function (day, includeOffDays) {
            const point = new DatePoint(day);
            return (includeOffDays || !point.isDayOff()) && _segments.some((s) => s.contains(point));
        },
        countSegment: function (from_, to_) {
            if (_segments.length == 0) return 0;
            if (from_.goesBefore(_segments[0].dateFrom))
                from_ = _segments[0].dateFrom;
            if (_segments[_segments.length - 1].dateTo.goesBefore(to_))
                to_ = _segments[_segments.length - 1].dateTo;

            var result = 0;
            var self = this;
            var segmentSpan = new DateSpan(from_, to_);
            segmentSpan.iterDays((date) => result += self.checkDay(date));
            return result;
        },
        checkIntersection: (dateFrom_, dateTo_) => {
            var span = new DateSpan(dateFrom_, dateTo_);
            return _segments.some((s) => s.intersects(span));
        }
    };
}

function getDate(datePicker, returnLegacyDate) {
    returnLegacyDate = returnLegacyDate || false;
    try {
        datePicker = $(datePicker);

        var format = datePicker.datepicker("option", "dateFormat"),
            text = datePicker.val(),
            settings = datePicker.datepicker("option", "settings");

        const date = $.datepicker.parseDate(format, text, settings);
        return returnLegacyDate ? date : new DatePoint(date, true);
    } catch {
        return returnLegacyDate ? null : new DatePoint(null, true);
    }
}

function nextDateAfterWorkingDay(datePoint, additionalHolidayFunc) {
    var checker = (d) => d.isDayOff();
    if (additionalHolidayFunc !== undefined) {
        checker = function (d) {
            return d.isDayOff() || additionalHolidayFunc(d);
        };
    }
    const date = datePoint.copy();
    while (true) {
        var isWorkingDay = !checker(date);
        date.inc();

        if (isWorkingDay) {
            return date;
        }
    }
}

const emptyFunc = () => { };

function makeDateFromAlertWorker({
    slideUpDuration = 200,
    dateFromSelector = "#dateFrom",
    submitForm = emptyFunc,
    dateFromAlertSelector = "#dateFromAlert",
    alertAcceptSelector = "#dateFromAlertAccept",
    isDynamicSubmit = false,
    minCheckDate = "01.01.1800",
    maxCheckDate = "01.01.2999",
    crownPeriods = undefined,
    isDayBeforeWeekend = false,
    isDayCrownHoliday = false,
    isWeekend = false,
    calculator = null
}) {
    var elem = $(dateFromAlertSelector);
    var alertAcceptElem = $(alertAcceptSelector);
    var shown = false;

    var config = {
        dateFrom: {tmp: null, frozen: null},
        prevDate: {tmp: null, frozen: null},
        nextPrevDateFrom: {tmp: null, frozen: null},
        nextDateFrom: {tmp: null, frozen: null},
        set: function (dateFrom_, prevDate_, nextDateFrom_) {
            if (dateFrom_ !== null) this.dateFrom.tmp = dateFrom_;
            if (prevDate_ !== null) this.prevDate.tmp = prevDate_;
            if (nextDateFrom_ !== null) {
                this.nextDateFrom.tmp = nextDateFrom_;
                this.nextPrevDateFrom.tmp = new DatePoint(nextDateFrom_).dec().date;
            }
        },
        freeze: function () {
            [this.dateFrom, this.prevDate, this.nextPrevDateFrom, this.nextDateFrom].forEach(function (x) {
                x.frozen = x.tmp;
            });
            elem.data("stateDayFrozen", elem.data("stateDay"));
        }
    };

    function previousDateBeforeWeekend(date) {
        var currDate = date.copy();
        while (true) {
            var isWorkingDay = !currDate.isDayOff();
            if (isWorkingDay) {
                return currDate;
            }
            currDate.dec();
        }
    }

    $(".variant-header").on("click", function (e) {
        if (e.target.tagName == "A")
            return;

        function updateVariant(elem) {
            var parent = elem.parentElement;
            var content = $(parent).find(".variant-content");
            if (!elem.classList.contains("close")) {
                content.slideDown();
            } else {
                content.slideUp(slideUpDuration);
            }
        }
        var self = this;
        var close = !self.classList.contains("close");
        $(".variant-header").addClass("close").each(function () {
            if (this === self && !close) {
                this.classList.remove("close");
            }
            updateVariant(this);
        });
    });

    alertAcceptElem.on("click", function () {
        var newDate = new DatePoint($(this).data("newDate"));
        const oldDate = new DatePoint($(dateFromSelector)[0].date);
        if (newDate.valid) {
            config.set(null, null, newDate.date);
            config.freeze();
            $(dateFromSelector)[0].date = newDate.date;
            const dateFromPoint = new DatePoint(config.dateFrom.frozen);
            elem.find(".datefrom-alert-val").html(dateFromPoint.toLocaleFormat());
            elem.find(".datefrom-alert-val-day").html(dateFromPoint.getDayName());
            elem.data("crownButton", this.classList.contains("crown"));
        }

        elem.slideUp(slideUpDuration);

        if (isDynamicSubmit) {
            elem.data("state", "accept");
            submitForm();
        }
        else {
            submitForm();
            elem.data("state", "accept");
        }

        if (!crownSegmWorker.checkDay(newDate.date) && crownSegmWorker.checkDay(nextDateAfterWorkingDay(oldDate).date) && oldDate.copy().dec().isDayOff())
            elem.data("stateDay", "crownOff");
        else if (crownSegmWorker.checkDay(newDate.date))
            elem.data("stateDay", "");

        if (calculator !== null) {
            calculator.sendStatisticMessage("Check_Day_Off");
        }

    });

    elem.find(".close-button").on("click", function () {
        elem.slideUp(slideUpDuration);
        elem.data("state", "closed");
        config.freeze();

        if (isDynamicSubmit) {
            submitForm();
        }
        return false;
    });

    var isCrown = crownPeriods === undefined ? false : true;
    var crownSegmWorker = makeDateSegmentWorker(crownPeriods || []);

    function checkCrown(dateFrom, equalityCheck, hideCovidInfo) {
        if (hideCovidInfo) return;
        var prevDatePoint = new DatePoint(dateFrom);
        prevDatePoint.dec();

        if (prevDatePoint.isDayOff() || crownSegmWorker.checkDay(prevDatePoint.date)) {
            var nextDateFromPoint = nextDateAfterWorkingDay(new DatePoint(dateFrom), function (d) { return crownSegmWorker.checkDay(d); });
            var nextPrevDateFromPoint = nextDateFromPoint.copy().dec();

            var result = (equalityCheck === undefined || nextDateFromPoint.toLocaleFormat() !== new DatePoint(equalityCheck).toLocaleFormat());
            if (result) {
                $(".datefrom-alert-new-prev-crown").html(nextPrevDateFromPoint.toLocaleFormat());
                $(".datefrom-alert-new-val-crown").html(nextDateFromPoint.toLocaleFormat());
                config.set(null, null, nextDateFromPoint.date);
                alertAcceptElem.each(function () {
                    if (this.classList.contains("crown")) {
                        $(this).data("newDate", nextDateFromPoint.toLocaleFormat());
                    }
                });
            }
            return result;
        }

        return false;
    }

    return {
        show: function () {
            shown = true;
            elem.slideDown();
            if (calculator !== null) {
                calculator.sendStatisticMessage("Show_Day_Off");
            }
        },
        shown: function () { return shown; },
        hide: function () { shown = false; elem.slideUp(slideUpDuration); },
        reset: function () { shown = false; elem.hide(); },
        getElem: function () { return elem; },
        getState: function () { return elem.data("state"); },
        getFrozenDateFrom: function () { return new DatePoint(config.dateFrom.frozen); },
        isVisible: function () { return elem.is(":visible"); },
        isAccepted: function () { return (elem.data("state") === "accept"); },
        isAcceptedCrown: function () { return (elem.data("crownButton")); },
        isClosed: function () { return (elem.data("state") === "closed"); },
        isDayCrown: function (frozen) { return frozen ? (elem.data("stateDayFrozen") == "crown") : (elem.data("stateDay") == "crown"); },
        isDayOffCrown: function (frozen) { return frozen ? (elem.data("stateDayFrozen") == "crownOff") : (elem.data("stateDay") == "crownOff"); },
        render: function (freeze) {  // this parameter useful for force freeze (card not closed, print, for example)
            if (freeze) {
                config.freeze();
            }
            var dateFromPoint = new DatePoint(config.dateFrom.frozen);
            var prevDatePoint = new DatePoint(config.prevDate.frozen);
            var nextPrevDateFromPoint = new DatePoint(config.nextPrevDateFrom.frozen);
            var nextDateFromPoint = new DatePoint(config.nextDateFrom.frozen);
            $(".datefrom-alert-val-render").html(dateFromPoint.toLocaleFormat());
            $(".datefrom-alert-val-day-render").html(dateFromPoint.getDayName());
            $(".datefrom-alert-prev-render").html(prevDatePoint.toLocaleFormat());
            $(".datefrom-alert-prev-day-render").html(prevDatePoint.getDayName());
            $(".datefrom-alert-new-prev-render").html(nextPrevDateFromPoint.toLocaleFormat());
            $(".datefrom-alert-new-val-render").html(nextDateFromPoint.toLocaleFormat());
            $(".datefrom-alert-new-val-day-render").html(nextDateFromPoint.getDayName());
        },
        check: function (dateFrom, hideCovidInfo) {
            const dateFromPoint = new DatePoint(dateFrom, true);
            if (!dateFrom || dateFromPoint.goesBefore(minCheckDate) || new DatePoint(maxCheckDate).goesBefore(dateFromPoint)) {
                this.hide();
                return;
            }

            elem.data("state", "");
            elem.data("stateDay", "");
            if (dateFromPoint.valid) {
                if (isDayBeforeWeekend) {
                    var currentDate = dateFromPoint;
                    if (currentDate.isDayOff()) {
                        var previousDatePoint = previousDateBeforeWeekend(dateFromPoint);
                        config.set(dateFromPoint.date, null, previousDatePoint.date);
                        alertAcceptElem.data("newDate", previousDatePoint.toLocaleFormat());
                        elem.find(".datefrom-alert-val").html(dateFromPoint.toLocaleFormat());
                        elem.find(".datefrom-alert-val-day").html(dateFromPoint.getDayName());
                        elem.find(".datefrom-alert-new-val").html(previousDatePoint.toLocaleFormat());
                        elem.find(".datefrom-alert-new-val-day").html(previousDatePoint.getDayName());
                        this.show();
                    } else {
                        this.hide();
                    }
                }
                else if (isWeekend) {
                    var prevDatePoint = dateFromPoint.copy().dec();
                    if (dateFromPoint.isDayOff() || checkCrown(dateFromPoint.date)) {
                        config.set(dateFromPoint.date, null, null);
                        elem.find(".datefrom-alert-val").html(dateFromPoint.toLocaleFormat());
                        elem.find(".datefrom-alert-val-day").html(dateFromPoint.getDayName());
                    }

                    if (dateFromPoint.isDayOff()) {
                        var nextDateFromPoint = nextDateAfterWorkingDay(dateFromPoint);
                        var workingDateFromPoint = nextDateFromPoint.copy().dec();
                        alertAcceptElem.data("newDate", workingDateFromPoint.toLocaleFormat());
                        elem.find(".datefrom-alert-new-val").html(workingDateFromPoint.toLocaleFormat());

                        if (isCrown && checkCrown(dateFromPoint.date, nextDateFromPoint.date, hideCovidInfo) && !hideCovidInfo) {
                            $(".info-block.variants").show();
                            $(".info-block.single").hide();
                            elem.data("stateDay", "crownOff");
                        } else if (isCrown) {
                            if (dateFromPoint.date.getFullYear() < 2023) {
                                $(".info-block-content .normal-cases-before-2023").show();
                                $(".info-block-content .normal-cases").hide();
                            } else {
                                $(".info-block-content .normal-cases-before-2023").hide();
                                $(".info-block-content .normal-cases").show();
                            }
                            $(".info-block-content .crown-case").hide();
                            $(".info-block.variants").hide();
                            $(".info-block.single").show();
                            if (crownSegmWorker.checkDay(dateFromPoint))
                                elem.data("stateDay", "crown");
                        }

                        config.set(null, null, workingDateFromPoint.date);

                        this.show();
                    } else {
                        if (isCrown && checkCrown(dateFromPoint.date) && !hideCovidInfo) {
                            $(".info-block-content .normal-cases-before-2023").hide();
                            $(".info-block-content .normal-cases").hide();
                            $(".info-block-content .crown-case").show();
                            $(".info-block.variants").hide();
                            $(".info-block.single").show();
                            elem.data("stateDay", "crown");
                            if (!isDayCrownHoliday)
                                this.show();
                            else
                                this.hide();
                        } else {
                            this.hide();
                        }
                    }
                } else {
                    prevDatePoint = dateFromPoint.copy().dec();
                    if (prevDatePoint.isDayOff() || checkCrown(dateFromPoint.date)) {
                        config.set(dateFromPoint.date, prevDatePoint.date, null);
                        elem.find(".datefrom-alert-val").html(dateFromPoint.toLocaleFormat());
                        elem.find(".datefrom-alert-val-day").html(dateFromPoint.getDayName());
                        elem.find(".datefrom-alert-prev").html(prevDatePoint.toLocaleFormat());
                        elem.find(".datefrom-alert-prev-day").html(prevDatePoint.getDayName());
                    }

                    if (prevDatePoint.isDayOff()) {
                        nextDateFromPoint = nextDateAfterWorkingDay(dateFromPoint);
                        var nextPrevDateFromPoint = nextDateFromPoint.copy().dec();

                        alertAcceptElem.data("newDate", nextDateFromPoint.toLocaleFormat());

                        elem.find(".datefrom-alert-new-prev").html(nextPrevDateFromPoint.toLocaleFormat());
                        elem.find(".datefrom-alert-new-val").html(nextDateFromPoint.toLocaleFormat());

                        if (isCrown && checkCrown(dateFromPoint.date, nextDateFromPoint.date, hideCovidInfo) && !hideCovidInfo) {
                            $(".info-block.variants").show();
                            $(".info-block.single").hide();
                            elem.data("stateDay", "crownOff");
                        } else if (isCrown) {
                            if (dateFromPoint.date.getFullYear() < 2023) {
                                $(".info-block-content .normal-cases-before-2023").show();
                                $(".info-block-content .normal-cases").hide();
                            } else {
                                $(".info-block-content .normal-cases-before-2023").hide();
                                $(".info-block-content .normal-cases").show();
                            }
                            $(".info-block-content .crown-case").hide();
                            $(".info-block.variants").hide();
                            $(".info-block.single").show();
                            if (crownSegmWorker.checkDay(dateFromPoint))
                                elem.data("stateDay", "crown");
                        }

                        config.set(null, null, nextDateFromPoint.date);

                        this.show();
                    } else {
                        if (isCrown && checkCrown(dateFromPoint.date) && !hideCovidInfo) {
                            $(".info-block-content .normal-cases-before-2023").hide();
                            $(".info-block-content .normal-cases").hide();
                            $(".info-block-content .crown-case").show();
                            $(".info-block.variants").hide();
                            $(".info-block.single").show();
                            elem.data("stateDay", "crown");
                            if (!isDayCrownHoliday)
                                this.show();
                            else
                                this.hide();
                        } else {
                            this.hide();
                        }
                    }
                }
            } else {
                this.hide();
            }
        }
    };
}

function makeDateInFutureAlertWorker({
    slideUpDuration = 200,
    submitForm = emptyFunc,
    isDynamicSubmit = false,
    dateToFutureAlertSelector = "#dateToFutureAlert",
    calculator = null
}) {
    var elem = $(dateToFutureAlertSelector);
    var shown = false;

    elem.find(".close-button").on("click", function (e) {
        elem.slideUp(slideUpDuration);
        elem.data("state", "closed");

        if (isDynamicSubmit) {
            submitForm();
        }
        e.preventDefault();
    });

    return {
        show: function () {
            shown = true;
            elem.slideDown();

            if (calculator !== null) {
                calculator.sendStatisticMessage("Date_Future");
            }
        },
        shown: function () { return shown; },
        hide: function () { shown = false; elem.slideUp(slideUpDuration); },
        reset: function () { shown = false; elem.hide(); },
        getElem: function () { return elem; },
        getState: function () { return elem.data("state"); },
        isVisible: function () { return elem.is(":visible"); },
        isAccepted: function () { return shown && (this.getState() === "accept"); },
        isClosed: function () { return shown && (this.getState() === "closed"); },
        check: function (dateTo) {
            elem.data("state", "");

            if (dateTo && dateTo.getDate() && (new DatePoint(dateTo).date > new DatePoint().date))
                this.show();
            else {
                this.hide();
            }
        }
    };
}


function distinctDates(dates) {
    if (!dates || dates.length == 0) {
        return dates;
    }
    if (dates[0] instanceof Date) {
        return dates
            .map(function (s) { return s.toString(); })
            .filter(tools.distinct)
            .map(function (s) { return new Date(s); });
    }
    else if (dates[0] instanceof DatePoint) {
        return dates
            .map(function (s) { return s.toLocaleFormat(); })
            .filter(tools.distinct)
            .map(function (s) { return new DatePoint(s); });
    } else {
        throw new Error("Wrong type of elements. Must be some of date formats.");
    }
}

function holidayChecker(date) {
    return new DatePoint(date).isDayOff();
}

function getTheFirstOfJanuary(date) {
    return new Date(date.getFullYear(), 0, 0);
}


export {DatePoint, DateSpan, getDate, distinctDates, makeDateInFutureAlertWorker, makeDateFromAlertWorker, makeDateSegmentWorker, nextDateAfterWorkingDay, holidayChecker};
